/*
 * ******************************************************
 * Copyright (c) 2022 Jenke & Rauschert GbR <info@aiandor.de> - All Rights Reserved
 *
 * This file is part of a software product owned by the Jenke & Rauschert GbR.
 * Unauthorized distribution of this file is strictly prohibited.
 * ******************************************************
 */

import {useAppDispatch, useAppSelector} from "../store";
import {Button, Drawer, Form, Input, Menu, Modal, Select, Space} from "antd";
import {useEffect, useState} from "react";
import {closeDrawer, openDrawer} from "../reducers/drawers-reducer";
import {triggerReload} from "../reducers/reloadsignal-reducer";
import {patch, post, del, get} from "../core/request";
import {IconSet} from "../components/icons";
import {Dropdown} from "antd";
import {CustomButton} from "../components/buttons";

import {Person, User} from "../core/datamodel";
import {useHistory} from "react-router-dom";
import Password from "antd/es/input/Password";
import CustomSelect from "../components/select";

type Class = User
const stack_selector = (state: any) => state.drawer.user
const uri = "users"
const key = "user"
const title = "Nutzer"

interface DrawerProps {
    size?: 'default' | 'large'
}


export function UserDrawer(props: DrawerProps) {
    const history = useHistory();
    const stack = useAppSelector(stack_selector)
    const drawer_stack = useAppSelector(state => state.drawer.stack)
    const dispatch = useAppDispatch();
    const [object, setObject] = useState({} as Class);
    const [form] = Form.useForm();

    const personReloadSignal = useAppSelector(state => state.reloadSignals.person)
    const [persons, setPersons] = useState([])


    const isNew = () => !(!!object?.props?.uuid)

    useEffect(() => {
        get("persons").then(
            (response) => setPersons(response.data.persons),
            (e) => console.error(e)
        )
    }, [stack, personReloadSignal])

    useEffect(() => {
        const object = stack[stack.length - 1] as Class
        form.resetFields()
        form.setFieldsValue({
            ...object?.props,
            person: object?.props?.person ? object?.props?.person.props.uuid : undefined,
        })
        setObject(object)
    }, [stack, form])

    const onFormFinish = () => {
        let values = form.getFieldsValue()
        object.update({
            ...values,
            person: values.person ? new Person({'uuid': values.person}) : null,
        })

        let req: JQueryPromise<any>;
        if (isNew()) {
            req = post(uri, object.dump())
        } else {
            req = patch(uri + "/" + object.props.uuid, object.dump())
        }

        req.then(() => {
                dispatch(triggerReload(key))
                dispatch(closeDrawer(key))
            },
            (e) => {
                console.error(e);
            })
    }

    const onDisable = () => {
        object.update({'disabled': !object.props.disabled})
        patch(uri + "/" + object.props.uuid,
            {
                'uuid': object.props.uuid,
                'disabled': object.props.disabled
            }
        ).then(
            () => {
                dispatch(triggerReload(key))
                dispatch(closeDrawer(key))
            },
            (e) => {
                console.error(e);
            })
    }

    const onDelete = () => {
        Modal.confirm({
            title: "Eintrag wirklich gelöscht werden?",
            content: "Diese Aktion kann nicht rückgängig gemacht werden!",
            maskClosable: true,
            cancelText: 'Abbrechen',
            autoFocusButton: 'cancel',
            okText: 'Löschen',
            okType: 'danger',
            onOk: () => {
                del(uri + "/" + object.props.uuid)
                    .then(
                        () => {
                            history.goBack()
                            dispatch(triggerReload(key))
                            dispatch(closeDrawer(key))
                        },
                        (e) => {
                            console.error(e);
                        })
                return false;
            }
        })
    }

    const onDrawerClose = (e: any) => {
        if (e.type === 'click' &&
            (e.target as Element).className === "ant-drawer-mask"
        ) {
            Modal.confirm({
                title: "Fenster wirklich schließen?",
                content: "Alle Änderungen gehen verloren!",
                maskClosable: true,
                cancelText: 'Weiter bearbeiten',
                okText: 'Änderungen verwerfen',
                autoFocusButton: 'cancel',
                onOk: () => {
                    dispatch(closeDrawer(key));
                    return false;
                }
            })
        } else {
            dispatch(closeDrawer(key))
        }

    }

    const personOptions = (
        <>
            {
                persons.map(
                    (person: { [key: string]: string }) =>
                        <Select.Option
                            key={person.uuid}
                            value={person.uuid}
                        >
                            {person.repr}
                        </Select.Option>)
            }
        </>
    )

    const groupOptions = (
        <>
            {
                ["admin", "mpe"].map(
                    (group: string) =>
                        <Select.Option
                            key={group}
                            value={group}
                        >
                            {group}
                        </Select.Option>)
            }
        </>
    )

    const form_layout = {
        labelCol: {span: 4},
        wrapperCol: {span: 18},
    };

    const form_element = (
        <Form
            form={form}
            onFinish={onFormFinish}
            {...form_layout}
        >
            <Form.Item
                label="Nutzername"
                name="username"
                required
                rules={[{required: true, message: "Bitte Username eingeben!"}]}
            >
                <Input name='username' placeholder="Nutzername"/>
            </Form.Item>

            <Form.Item label="Kontakt" style={{margin: 0}} required>
                <Form.Item
                    style={{display: 'inline-block', width: 'calc(100% - 32px - 8px)'}}
                    name="person"
                    rules={[{required: true, message: 'Bitte einen Kontakt zuweisen!'}]}
                >
                    <CustomSelect searchable placeholder="Kontakt wählen" loading={persons.length === 0}>
                        {personOptions}
                    </CustomSelect>
                </Form.Item>
                <Form.Item style={{display: 'inline-block', margin: '0 0 0 8px'}}>
                    <CustomButton mode='add'
                                  size="small"                                  tooltip="Neue Person hinzufügen"
                                  onClick={
                                      () => dispatch(openDrawer(
                                          {key: 'person', object: new Person({})}
                                      ))
                                  }/>
                </Form.Item>
            </Form.Item>

            <Form.Item
                label="Gruppen"
                name="groups"
            >
                <Select mode="tags" placeholder="Gruppen" children={groupOptions}/>
            </Form.Item>

            <Form.Item
                label="Passwort"
                name="password"
            >
                <Password name='password' placeholder="Passwort"/>
            </Form.Item>

            {/* Buttons */}
            <Form.Item
                label=" "
                colon={false}
            >
                <Form.Item
                    style={{display: 'inline-block'}}
                >
                    <Button type="primary" htmlType="submit">
                        <IconSet type='save'/>&nbsp;Speichern
                    </Button>
                </Form.Item>
                <Form.Item
                    style={{display: 'inline-block', margin: '0 24px'}}
                >
                    <Button onClick={onDrawerClose}>
                        <IconSet type='cancel'/>&nbsp;Änderungen verwerfen
                    </Button>
                </Form.Item>
            </Form.Item>
        </Form>
    )

    const menu = (
        isNew() ? (<></>) : (
            <Dropdown
                arrow
                overlay={
                    <Menu style={{padding: 12}} items={[
                        {
                            key: "disable",
                            onClick: onDisable,
                            icon: <IconSet type='disable'/>,
                            label: !!object?.props?.disabled ? ("Reaktivieren") : ("Deaktivieren")
                        },
                        {
                            key: "delete",
                            danger: true,
                            disabled: !object?.props?.is_deletable,
                            onClick: onDelete,
                            icon: <IconSet type='delete'/>,
                            label: "Löschen"
                        },
                    ]}
                    />
                }
                placement="bottomRight"
                className='button-edit'
            >
                <CustomButton mode='more'/>
            </Dropdown>)
    )

    return (
        <Drawer
            open={stack.length > 0 && drawer_stack[drawer_stack.length - 1] === key}
            size={props.size}
            onClose={onDrawerClose}
            forceRender={true}
            zIndex={drawer_stack[drawer_stack.length - 1] === key ? 1000 : 900}
            title={
                <Space direction="horizontal" style={{width: '100%', justifyContent: 'center'}}>
                    <h1 style={{marginBottom: 0}}>{title + (isNew() ? " hinzufügen" : " bearbeiten")}</h1>
                </Space>
            }
            extra={menu}
        >
            {form_element}
        </Drawer>
    )
}


UserDrawer.defaultProps = {
    size: 'large'
}